import React, { useState } from "react";
import { ArrowRight, Heart, Lightbulb, Star, Trash2 } from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import { isFavoriteTeamPredictionActivity } from "../../utils/activityNavigation";

function formatAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 45) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return "Yesterday";
  return `${Math.floor(s / 86400)}d ago`;
}

const FollowingPredictionActivityCard = ({
  item,
  unread,
  onMarkRead,
  onViewPrediction,
  onDelete,
}) => {
  const md = item?.metadata || {};
  const isFavoriteTeam = isFavoriteTeamPredictionActivity(item);
  const creatorName = md.creatorName || "Someone you follow";
  const favoriteTeamName = md.favoriteTeamName || md.homeName || "Your team";
  const homeName = md.homeName || "Home";
  const awayName = md.awayName || "Away";
  const outcomePreview = md.outcomePreview || "Tip";
  const leadConfidence = Number(md.leadConfidence) || 0;
  const isPremium = Boolean(md.isPremium);
  const price = Number(md.price) || 0;

  const [avatarFailed, setAvatarFailed] = useState(false);

  return (
    <article
      className={`act-follow-pred ${unread ? "act-follow-pred-unread" : ""}`}
      onClick={() => unread && onMarkRead?.(item._id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && unread) onMarkRead?.(item._id);
      }}
      role="button"
      tabIndex={0}
    >
      <div className="act-follow-pred-stripe" aria-hidden />

      <div className="act-follow-pred-inner">
        <div className="act-follow-pred-head">
          <span className="act-follow-pred-badge">
            {isFavoriteTeam ? (
              <>
                <Star size={10} aria-hidden />
                Favorite team
              </>
            ) : (
              <>
                <Heart size={10} aria-hidden />
                Following
              </>
            )}
          </span>
          <span className="act-follow-pred-time">{formatAgo(item.createdAt)}</span>
        </div>

        <div className="act-follow-pred-hero">
          <div className="act-follow-pred-avatar-wrap">
            {!avatarFailed ? (
              <img
                className="act-follow-pred-avatar"
                src={generateAvatarUrl(creatorName)}
                alt=""
                width={42}
                height={42}
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className="act-follow-pred-avatar act-follow-pred-avatar-fallback">
                {creatorName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="act-follow-pred-avatar-dot" aria-hidden>
              <Lightbulb size={8} />
            </span>
          </div>

          <div className="act-follow-pred-copy">
            <h3 className="act-follow-pred-title">
              {isFavoriteTeam ? (
                <>
                  <span className="act-follow-pred-name">{favoriteTeamName}</span>
                  {" has a new prediction"}
                </>
              ) : (
                <>
                  <span className="act-follow-pred-name">{creatorName}</span>
                  {" posted a prediction"}
                </>
              )}
            </h3>
            <p className="act-follow-pred-match">
              {isFavoriteTeam
                ? `${creatorName} · ${homeName} vs ${awayName}`
                : `${homeName} vs ${awayName}`}
            </p>
          </div>

          {unread ? <span className="act-row-dot" aria-label="Unread" /> : null}
        </div>

        <div className="act-follow-pred-details">
          <div className="act-follow-pred-outcome">
            <span className="act-follow-pred-outcome-label">Pick</span>
            <span className="act-follow-pred-outcome-text">
              {outcomePreview}
              {leadConfidence > 0 ? ` · ${leadConfidence}%` : ""}
            </span>
          </div>
          <div className="act-follow-pred-tier">
            <span className="act-follow-pred-tier-label">
              {isPremium ? "Premium" : "Free"}
            </span>
            <strong className="tabular-nums">
              {isPremium ? `${price.toLocaleString()} COIN` : "Open"}
            </strong>
          </div>
        </div>

        <button
          type="button"
          className="act-follow-pred-cta"
          onClick={(e) => {
            e.stopPropagation();
            onViewPrediction?.(item);
          }}
        >
          <span>View prediction</span>
          <ArrowRight size={12} aria-hidden />
        </button>
      </div>

      <button
        type="button"
        className="act-row-delete"
        aria-label="Remove activity"
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.(item._id);
        }}
      >
        <Trash2 size={14} />
      </button>
    </article>
  );
};

export default FollowingPredictionActivityCard;
